// src/components/TermsPage.tsx
import React from 'react';
import { Card, CardContent } from './ui/card';

const TermsPage: React.FC = () => {
  return (
    <div className="container mx-auto max-w-3xl px-4 py-8">
      <Card>
        <CardContent className="p-6 md:p-8">
          <div className="prose prose-slate max-w-none">
            <h1>Terms of Use for WhatSayUs</h1>
            <p>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

            <h2>1. Agreement to Terms</h2>
            <p>By accessing or using the WhatSayUs website, services, or applications (collectively, the "Service"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, you should not use the Service.</p>
            <p>WhatSayUs provides a platform for facilitating group decision-making through AI-assisted conversations and analysis.</p>

            <h2>2. Eligibility</h2>
            <p>You must be at least 13 years of age to use the Service. By using the Service, you represent and warrant that you meet this requirement. If you are between the ages of 13 and 18 (or the age of legal majority in your jurisdiction), you may only use the Service with the consent and supervision of a parent or legal guardian who agrees to be bound by these Terms.</p>

            <h2>3. User Accounts</h2>
            <h3>3.1 Account Creation</h3>
            <p>Some features of the Service may require an account. You can create an account by signing in with Google or, for certain features, by providing a name to participate anonymously.</p>

            <h3>3.2 Account Responsibilities</h3>
            <p>You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.</p>

            <h2>4. User Content</h2>
            <h3>4.1 Content Submission</h3>
            <p>When you submit content through the Service, including messages, opinions, and other materials ("User Content"), you grant WhatSayUs a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute such content in connection with providing the Service.</p>

            <h3>4.2 Content Restrictions</h3>
            <p>You agree not to submit User Content that:</p>
            <ul>
              <li>Is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or invasive of another's privacy</li>
              <li>Infringes on intellectual property rights of others</li>
              <li>Contains software viruses or any other code designed to interrupt, destroy, or limit the functionality of any computer software or hardware</li>
              <li>Constitutes unsolicited or unauthorized advertising, promotional materials, or spam</li>
              <li>Impersonates any person or entity or falsely states or misrepresents your affiliation with a person or entity</li>
            </ul>

            <h3>4.3 Content Removal</h3>
            <p>WhatSayUs reserves the right to remove any User Content for any reason without notice.</p>

            <h2>5. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              <li>Attempt to gain unauthorized access to any portion of the Service</li>
              <li>Use any automated means to access the Service</li>
              <li>Sell, trade, or transfer your account or access to the Service</li>
            </ul>

            <h2>6. Service Limitations and Modifications</h2>
            <h3>6.1 Availability</h3>
            <p>WhatSayUs does not guarantee that the Service will be available at all times or that it will be uninterrupted or error-free.</p>

            <h3>6.2 Modifications</h3>
            <p>WhatSayUs reserves the right to modify or discontinue the Service, temporarily or permanently, with or without notice. You agree that WhatSayUs shall not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.</p>

            <h2>7. Disclaimer of Warranties</h2>
            <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>
            <p>WhatSayUs does not warrant that:</p>
            <ul>
              <li>The Service will meet your requirements</li>
              <li>The Service will be uninterrupted, timely, secure, or error-free</li>
              <li>The results obtained from the use of the Service will be accurate or reliable</li>
              <li>The quality of any products, services, information, or other material purchased or obtained through the Service will meet your expectations</li>
            </ul>

            <h2>8. Limitation of Liability</h2>
            <p>IN NO EVENT SHALL WhatSayUs, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:</p>
            <ul>
              <li>Your access to or use of or inability to access or use the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>

            <h2>9. Indemnification</h2>
            <p>You agree to defend, indemnify, and hold harmless WhatSayUs and its officers, directors, employees, and agents, from and against any claims, liabilities, damages, losses, and expenses, including without limitation reasonable attorney fees and costs, arising out of or in any way connected with your access to or use of the Service, your violation of these Terms, or your violation of any rights of another.</p>

            <h2>10. Third-Party Links and Services</h2>
            <p>The Service may contain links to third-party websites or services that are not owned or controlled by WhatSayUs. WhatSayUs has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You further acknowledge and agree that WhatSayUs shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such websites or services.</p>

            <h2>11. Termination</h2>
            <p>WhatSayUs may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including without limitation if you breach these Terms. Upon termination, your right to use the Service will immediately cease.</p>

            <h2>12. Changes to Terms</h2>
            <p>WhatSayUs reserves the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>

            <h2>13. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.</p>

            <h2>14. Contact Information</h2>
            <p>If you have any questions about these Terms, please contact us at hello@whatsayus.com.</p>

            <h2>15. Severability</h2>
            <p>If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall be enforced.</p>

            <h2>16. Entire Agreement</h2>
            <p>These Terms constitute the entire agreement between you and WhatSayUs regarding the Service and supersede all prior agreements and understandings, whether written or oral, regarding the Service.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsPage;